import React, { useState, useEffect } from 'react';
import { Plus, MapPin, Image as ImageIcon, ChevronRight, Trash2, Loader2, ArrowLeft, ArrowRight, Camera, RotateCcw, MessageSquare, Download, Settings, Check, Layers, Pencil, Tag, MoreVertical, Link, Eye } from 'lucide-react';
import { AppMode, RoomType, StagingStyle, UserProfile, ROOM_LABELS } from '../types';
import { trackEvent } from '../services/analytics';
import { api, API_BASE } from '../services/api';
import { ComparisonSlider } from './ComparisonSlider';
import { BulkGeneratePanel } from './BulkGeneratePanel';
import { SmartUpload } from './SmartUpload';
import { ConfirmDialog } from './admin/_primitives/ConfirmDialog';
import { PresentationLink } from './PresentationLink';
import { CORE_ROOMS, coverageDenominator, coveredRoomCount, roomCoveragePct } from '../src/rooms/coverage';

export interface Property {
  id: number;
  address: string;
  city: string | null;
  postal_code: string | null;
  created_at: string;
  render_count: number;
  last_render_at: string | null;
  latest_render: string | null;
  slug: string | null;
  staging_style: string | null;
  covered_rooms: string | null; // comma-separated: "Living Room,Kitchen"
  thumb_living_room: string | null;
  thumb_bedroom: string | null;
  thumb_kitchen: string | null;
  thumb_bathroom: string | null;
}

interface Render {
  id: number;
  property_id: number;
  room_type: string;
  mode: string;
  generation_type: string;
  staged_filename: string;
  original_filename?: string | null;
  feedback_text?: string | null;
  created_at: string;
}

interface Props {
  user: UserProfile;
  onSelect: (property: Property, preselectedRoom?: string, file?: File, style?: StagingStyle) => void;
  onBatchRestyle?: (property: Property, photos: { file: File; roomType: RoomType }[], style?: StagingStyle) => void;
  onCreditsUpdate?: (credits: number) => void;
  /**
   * STA-198: when set, PropertyManager opens the detail view for this property
   * and auto-mounts the SmartUpload panel so the OS file picker opens immediately.
   * Consumed once on mount; parent should clear after passing.
   */
  autoStartStagingFor?: Property | null;
  /**
   * STA-199: same as `autoStartStagingFor` but accepts only an id. Used when
   * the user lands on `/eigendommen/:id/upload` directly via URL — we don't
   * have the full Property object yet, so PropertyManager resolves it after
   * fetching the property list.
   */
  autoStartStagingForId?: number | null;
  /** Callback fired once after autoStartStagingFor has been consumed. */
  onAutoStartConsumed?: () => void;
}

// Core rooms shown as tiles/pills; Office is optional (STA-184) and appears in history.
const ALL_ROOMS: readonly string[] = CORE_ROOMS;
const ROOM_ABBREV: Record<string, string> = {
  'Living Room': 'WK', 'Bedroom': 'SL', 'Kitchen': 'KE', 'Bathroom': 'BA', 'Office': 'KA',
};
const MODE_LABELS: Record<string, string> = {
  FULL_RENOVATION: 'Renovatie + Staging', STAGING_ONLY: 'Staging',
};
const TYPE_LABELS: Record<string, { label: string; color: string }> = {
  initial:    { label: 'Restyling',   color: '#c8f000' },
  restyle:    { label: 'Herstijld',   color: '#fed7aa' },
  correction: { label: 'Correctie',   color: '#f3e8ff' },
};

const LABEL_KEY = 'stagery_export_label';

const timeAgo = (dt: string | null): string => {
  if (!dt) return 'Nooit';
  const diff = Date.now() - new Date(dt).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Zojuist';
  if (mins < 60) return `${mins} min geleden`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} uur geleden`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d geleden`;
  return new Date(dt).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

function parseCoveredRooms(covered: string | null): Set<string> {
  if (!covered) return new Set();
  return new Set(covered.split(','));
}

const PropertyTable: React.FC<{
  properties: Property[];
  onOpen: (p: Property) => void;
  onDelete: (id: number, e: React.MouseEvent) => void;
  onSelectRoom: (p: Property, room: string) => void;
  fmt: (dt: string | null) => string;
}> = ({ properties, onOpen, onDelete, onSelectRoom, fmt }) => {
  const [sortKey, setSortKey] = useState<'address' | 'coverage' | 'renders' | 'date'>('date');
  const [sortAsc, setSortAsc] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);

  useEffect(() => {
    const handler = () => { setOpenMenu(null); setMenuPos(null); };
    if (openMenu) document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [openMenu]);

  const toggleSort = (key: typeof sortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(true); }
  };

  const sorted = [...properties].sort((a, b) => {
    const dir = sortAsc ? 1 : -1;
    switch (sortKey) {
      case 'address': return dir * a.address.localeCompare(b.address);
      case 'coverage': return dir * (roomCoveragePct(parseCoveredRooms(a.covered_rooms)) - roomCoveragePct(parseCoveredRooms(b.covered_rooms)));
      case 'renders': return dir * (a.render_count - b.render_count);
      case 'date': return dir * ((a.last_render_at || a.created_at).localeCompare(b.last_render_at || b.created_at));
      default: return 0;
    }
  });

  const thClass = "px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wide cursor-pointer hover:text-accent transition-colors select-none";
  const arrow = (key: typeof sortKey) => sortKey === key ? (sortAsc ? ' ↑' : ' ↓') : '';

  return (
    <div className="rounded-lg overflow-hidden border border-border overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-stone text-ink">
            <th className={thClass} onClick={() => toggleSort('address')}>Adres{arrow('address')}</th>
            <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wide">Stad</th>
            <th className={thClass} onClick={() => toggleSort('coverage')}>Dekking{arrow('coverage')}</th>
            <th className={thClass} onClick={() => toggleSort('renders')}>Restylings{arrow('renders')}</th>
            <th className={thClass} onClick={() => toggleSort('date')}>Laatste{arrow('date')}</th>
            <th className="px-4 py-3 text-center text-[10px] font-bold uppercase tracking-wide">Presentatie</th>
            <th className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-wide">Acties</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map(p => {
            const covered = parseCoveredRooms(p.covered_rooms);
            const pct = roomCoveragePct(covered);
            return (
              <tr key={p.id} className="border-b border-stone-100 hover:bg-stone-50/50 cursor-pointer" onClick={() => onOpen(p)}>
                <td className="px-4 py-3 font-semibold">{p.address}</td>
                <td className="px-4 py-3 text-ink/50">{p.city || '-'}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-0.5">
                      {ALL_ROOMS.map(room => (
                        <span key={room} className={`w-5 h-5 text-[8px] font-bold flex items-center justify-center ${
                          covered.has(room) ? 'bg-accent/40 text-ink/70' : 'bg-stone-100 text-ink/50'
                        }`} title={ROOM_LABELS[room]}
                          onClick={e => { e.stopPropagation(); if (!covered.has(room)) onSelectRoom(p, room); }}
                        >{ROOM_ABBREV[room]}</span>
                      ))}
                    </div>
                    <span className="text-[10px] text-ink/50">{pct}%</span>
                  </div>
                </td>
                <td className="px-4 py-3">{p.render_count}</td>
                <td className="px-4 py-3 text-ink/50 text-xs">{timeAgo(p.last_render_at || p.created_at)}</td>
                <td className="px-4 py-3 text-center">
                  <PresentationLink property={p} />
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (openMenu === String(p.id)) {
                        setOpenMenu(null);
                        setMenuPos(null);
                      } else {
                        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                        setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                        setOpenMenu(String(p.id));
                      }
                    }}
                    className="p-1.5 rounded-md border border-black/8 text-ink/60 hover:text-ink hover:border-black/15 hover:bg-black/5 transition-colors"
                    aria-label="Acties"
                  >
                    <MoreVertical size={14} />
                  </button>
                  {openMenu === String(p.id) && menuPos && (
                    <div
                      style={{ position: 'fixed', top: menuPos.top, right: menuPos.right, zIndex: 50 }}
                      className="bg-white border border-border rounded-md shadow-lg py-1 min-w-[160px]"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <PresentationLink property={p} />
                      <button onClick={(e) => { e.stopPropagation(); onOpen(p); setOpenMenu(null); setMenuPos(null); }} className="w-full text-left px-3 py-2 text-xs hover:bg-stone transition-colors flex items-center gap-2 rounded-md">
                        <Eye size={12} /> Bekijken
                      </button>
                      <div className="border-t border-border my-1" />
                      <button onClick={(e) => { e.stopPropagation(); onDelete(p.id, e); setOpenMenu(null); setMenuPos(null); }} className="w-full text-left px-3 py-2 text-xs text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2 rounded-md">
                        <Trash2 size={12} /> Verwijderen
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const PropertyDetailHeader: React.FC<{
  property: Property;
  exportLabel: string;
  onEditLabel: (v: string) => void;
  onSaveProperty: (address: string, city: string, postalCode: string) => Promise<void>;
  onBulk: () => void;
  onStartStaging: () => void;
  onDelete: () => void;
  onTogglePublic: () => void;
}> = ({ property, exportLabel, onEditLabel, onSaveProperty, onBulk, onStartStaging, onDelete, onTogglePublic }) => {
  const [editing, setEditing] = useState(false);
  const [addr, setAddr] = useState(property.address);
  const [cty, setCty] = useState(property.city || '');
  const [pc, setPc] = useState(property.postal_code || '');
  const [editLabel, setEditLabel] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detailMenu, setDetailMenu] = useState(false);

  useEffect(() => {
    const handler = () => setDetailMenu(false);
    if (detailMenu) document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [detailMenu]);

  const save = async () => {
    if (!addr.trim()) return;
    setSaving(true);
    await onSaveProperty(addr.trim(), cty.trim(), pc.trim());
    setSaving(false);
    setEditing(false);
  };

  return (
    <div className="mb-6">
      <div className="flex flex-col md:flex-row items-start justify-between gap-4">
        <div className="flex-1 min-w-0 w-full md:w-auto">
          {editing ? (
            <div className="space-y-2 max-w-sm">
              <div>
                <label className="block text-[10px] font-bold text-ink/50 uppercase tracking-wide mb-1">Adres</label>
                <input value={addr} onChange={e => setAddr(e.target.value)} autoFocus
                  className="w-full border border-border/30 p-2 text-sm focus:outline-none focus:border-border" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] font-bold text-ink/50 uppercase tracking-wide mb-1">Postcode</label>
                  <input value={pc} onChange={e => setPc(e.target.value)} placeholder="1012 AB"
                    className="w-full border border-border/30 p-2 text-sm focus:outline-none focus:border-border" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-ink/50 uppercase tracking-wide mb-1">Stad</label>
                  <input value={cty} onChange={e => setCty(e.target.value)}
                    className="w-full border border-border/30 p-2 text-sm focus:outline-none focus:border-border" />
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={save} disabled={saving}
                  className="bg-ink text-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide hover:bg-accent hover:text-ink transition-colors flex items-center gap-1">
                  {saving ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />} Opslaan
                </button>
                <button onClick={() => { setEditing(false); setAddr(property.address); setCty(property.city || ''); setPc(property.postal_code || ''); }}
                  className="text-[10px] font-bold uppercase tracking-wide text-ink/50 hover:text-ink px-3 py-1.5 border border-border/20">
                  Annuleer
                </button>
              </div>
            </div>
          ) : (
            <div className="group">
              <div className="flex items-center gap-2">
                <h1 className="font-sans text-3xl md:text-4xl font-bold tracking-[-0.02em] text-ink">{property.address}{property.city ? <span className="text-ink/40 font-semibold">, {property.city}</span> : null}</h1>
                <button onClick={() => setEditing(true)} className="text-ink/25 hover:text-accent transition-colors opacity-0 group-hover:opacity-100">
                  <Pencil size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={(e) => { e.stopPropagation(); onStartStaging(); }}
            className="flex items-center gap-2 bg-accent hover:bg-accent-hover text-white px-4 py-2.5 font-semibold text-sm rounded-lg shadow-[0_6px_18px_-6px_rgba(241,110,0,0.5)] hover:shadow-[0_10px_26px_-8px_rgba(241,110,0,0.55)] hover:-translate-y-0.5 transition-all">
            <Camera size={14} /> Start staging
          </button>
          <div className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); setDetailMenu(!detailMenu); }}
              className="p-2 rounded-lg hover:bg-black/5 transition-colors border border-black/8"
            >
              <MoreVertical size={14} />
            </button>
            {detailMenu && (
              <div className="absolute right-0 top-11 z-50 bg-white border border-border rounded-md shadow-lg py-1 min-w-[180px]">
                <button onClick={() => { setEditing(true); setDetailMenu(false); }} className="w-full text-left px-3 py-2 text-xs hover:bg-stone transition-colors flex items-center gap-2 rounded-md">
                  <Pencil size={12} /> Adres bewerken
                </button>
                {property.slug && (
                  <button onClick={() => { onTogglePublic(); setDetailMenu(false); }} className="w-full text-left px-3 py-2 text-xs hover:bg-stone transition-colors flex items-center gap-2 rounded-md">
                    <Eye size={12} /> Presentatiepagina
                  </button>
                )}
                <PresentationLink property={property} />
                <div className="border-t border-border my-1" />
                <button onClick={() => { onDelete(); setDetailMenu(false); }} className="w-full text-left px-3 py-2 text-xs text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2 rounded-md">
                  <Trash2 size={12} /> Woning verwijderen
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Export label - inline editable */}
      <div className="mt-3 flex items-center gap-2">
        <Tag size={12} className="text-ink/50" />
        {editLabel ? (
          <div className="flex items-center gap-1">
            <input value={exportLabel} onChange={e => onEditLabel(e.target.value)}
              className="border border-border/30 px-2 py-0.5 text-xs w-48 focus:outline-none focus:border-border" autoFocus />
            <button onClick={() => setEditLabel(false)} className="text-ink/50 hover:text-ink"><Check size={12} /></button>
          </div>
        ) : (
          <button onClick={() => setEditLabel(true)} className="text-[10px] text-ink/50 hover:text-ink transition-colors uppercase tracking-wide flex items-center gap-1">
            {exportLabel} <Pencil size={9} className="opacity-0 group-hover:opacity-100" />
          </button>
        )}
      </div>
    </div>
  );
};

const RoomScorecard: React.FC<{
  rooms: readonly string[];
  renders: Render[];
  onSelectRoom: (room: string) => void;
}> = ({ rooms, renders, onSelectRoom }) => {
  const coveredCount = rooms.filter(r => renders.some(rd => rd.room_type === r)).length;
  const pct = Math.round((coveredCount / rooms.length) * 100);

  return (
    <div className="bg-white border border-border p-5 mb-6 shadow-card">
      {/* Progress bar */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1 bg-stone-100 h-2.5 overflow-hidden">
          <div className="h-full bg-accent transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
        <span className="text-sm font-bold shrink-0">{pct}%</span>
        <span className="text-[10px] text-ink/50 uppercase tracking-wide shrink-0">compleet</span>
      </div>

      {/* Room rows */}
      <div className="divide-y divide-stone-100">
        {rooms.map(room => {
          const roomRenders = renders.filter(r => r.room_type === room);
          const done = roomRenders.length > 0;
          return (
            <div key={room} className="flex items-center gap-3 py-2.5">
              <div className={`w-5 h-5 flex items-center justify-center shrink-0 ${done ? 'bg-accent' : 'border border-border/20'}`}>
                {done && <Check size={12} strokeWidth={3} />}
              </div>
              <span className="text-sm font-semibold flex-1">{ROOM_LABELS[room]}</span>
              <span className="text-xs text-ink/50 w-20 text-right">
                {roomRenders.length > 0 ? `${roomRenders.length} restyling${roomRenders.length !== 1 ? 's' : ''}` : '-'}
              </span>
              <button
                onClick={() => onSelectRoom(room)}
                className={`text-[10px] font-bold uppercase tracking-wide px-3 py-1.5 transition-colors shrink-0 ${
                  done
                    ? 'text-ink/50 border border-border/20 hover:border-border hover:text-ink'
                    : 'bg-ink text-white hover:bg-accent hover:text-ink'
                }`}
              >
                {done ? '+ Nieuwe' : 'Start'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const PropertyManager: React.FC<Props> = ({ user, onSelect, onBatchRestyle, onCreditsUpdate, autoStartStagingFor, autoStartStagingForId, onAutoStartConsumed }) => {
  const [showBulk, setShowBulk] = useState(false);
  const [showSmartUpload, setShowSmartUpload] = useState(false);
  // STA-200: room pre-scoped for the SmartUpload panel. When set, classifier
  // results are forced to this room and only that ruimte-group is shown.
  const [smartUploadRoom, setSmartUploadRoom] = useState<RoomType | null>(null);
  const [listMode, setListMode] = useState<'grid' | 'table'>(() => (localStorage.getItem('er_list_mode_v2') as 'grid' | 'table') || 'grid');
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [address, setAddress] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingProperty, setDeletingProperty] = useState<Property | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Export label
  const [exportLabel, setExportLabel] = useState(() => localStorage.getItem(LABEL_KEY) || 'Digitale herinrichting');
  const [editingLabel, setEditingLabel] = useState(false);

  useEffect(() => { localStorage.setItem(LABEL_KEY, exportLabel); }, [exportLabel]);

  // Detail view
  const [detailProperty, setDetailProperty] = useState<Property | null>(null);
  const [detailRenders, setDetailRenders] = useState<Render[]>([]);
  const [rendersLoading, setRendersLoading] = useState(false);
  const [lightboxRender, setLightboxRender] = useState<Render | null>(null);
  const [regenFeedback, setRegenFeedback] = useState('');
  const [regenLoading, setRegenLoading] = useState(false);
  const [regenError, setRegenError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      setProperties(await api.get<Property[]>('/api/properties'));
    } catch {
      setError('Kon woningen niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // STA-198: when parent passes a property to auto-start staging on, open the
  // detail view and immediately mount the SmartUpload panel so the OS file
  // picker pops + user kan meteen meerdere foto's selecteren (batch flow).
  useEffect(() => {
    if (!autoStartStagingFor) return;
    setSmartUploadRoom(null);
    openDetail(autoStartStagingFor);
    setShowSmartUpload(true);
    onAutoStartConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStartStagingFor]);

  // STA-199: direct URL hit op /eigendommen/:id/upload — alleen id bekend.
  // Wacht tot de properties geladen zijn, resolve id naar Property en open
  // dezelfde batch flow als bovenstaande hook.
  useEffect(() => {
    if (autoStartStagingForId == null) return;
    if (loading) return;
    const match = properties.find(p => p.id === autoStartStagingForId);
    if (!match) return;
    setSmartUploadRoom(null);
    openDetail(match);
    setShowSmartUpload(true);
    onAutoStartConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStartStagingForId, loading, properties.length]);

  // STA-200: open detail view + start de batch flow pre-scoped op één ruimte.
  // Wordt aangeroepen vanuit alle "Start <ruimte>"-knoppen op lege ruimtes
  // (zowel grid- als tabel-weergave + RoomScorecard tiles).
  const startStagingForRoom = (p: Property, room: string) => {
    setSmartUploadRoom(room as RoomType);
    openDetail(p);
    setShowSmartUpload(true);
  };

  const openDetail = async (p: Property) => {
    setDetailProperty(p);
    setRendersLoading(true);
    try {
      setDetailRenders(await api.get<Render[]>(`/api/properties/${p.id}/renders`));
    } catch {
      setDetailRenders([]);
    } finally {
      setRendersLoading(false);
    }
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!address.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const newProp = await api.post<Property>('/api/properties', { address: address.trim(), city: city.trim() || undefined, postalCode: postalCode.trim() || undefined });
      const prevCount = properties.length;
      trackEvent('property_created', { isFirst: prevCount === 0 });
      setAddress(''); setCity(''); setPostalCode(''); setShowAdd(false);
      // Direct naar de nieuwe woning — opent detail + batch upload panel
      // (geen prescope; lege woning kan meerdere ruimtes tegelijk uploaden).
      openDetail(newProp);
      setShowSmartUpload(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Toevoegen mislukt');
    } finally {
      setSaving(false);
    }
  };

  const requestDelete = (property: Property) => {
    setDeleteError(null);
    setDeletingProperty(property);
  };

  const handleDelete = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const property = properties.find(p => p.id === id);
    if (property) requestDelete(property);
  };

  const confirmDelete = async () => {
    if (!deletingProperty || deleting) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.del(`/api/properties/${deletingProperty.id}`);
      if (detailProperty?.id === deletingProperty.id) setDetailProperty(null);
      setDeletingProperty(null);
      await load();
    } catch (err: unknown) {
      setDeleteError(err instanceof Error ? err.message : 'Verwijderen mislukt');
    } finally {
      setDeleting(false);
    }
  };

  const deleteDialog = (
    <ConfirmDialog
      open={!!deletingProperty}
      destructive
      title="Woning verwijderen"
      description={<>
        <p>Weet je zeker dat je <strong>{deletingProperty?.address}</strong> en alle bijbehorende restylings wilt verwijderen? Dit kan niet ongedaan worden gemaakt.</p>
        {deleteError && <p role="alert" className="mt-2 text-red-600">{deleteError}</p>}
      </>}
      confirmLabel="Verwijderen"
      cancelLabel="Annuleren"
      onConfirm={confirmDelete}
      onCancel={() => { if (!deleting) { setDeletingProperty(null); setDeleteError(null); } }}
    />
  );

  const fmt = (dt: string | null) => dt
    ? new Date(dt).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '-';

  const handleGalleryDownload = (render: Render) => {
    const label = exportLabel;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0);

      const fontSize = Math.max(12, Math.round(img.width * 0.018));
      ctx.font = `bold ${fontSize}px monospace`;
      const padding = Math.round(fontSize * 0.6);
      const textWidth = ctx.measureText(label).width;
      const boxW = textWidth + padding * 2;
      const boxH = fontSize + padding * 2;
      const x = img.width - boxW - padding;
      const y = img.height - boxH - padding;
      ctx.fillStyle = 'rgba(255,255,255,0.82)';
      ctx.fillRect(x, y, boxW, boxH);
      ctx.fillStyle = '#1a1a1a';
      ctx.fillText(label, x + padding, y + padding + fontSize * 0.85);

      canvas.toBlob(blob => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Stagery_Staged_${Date.now()}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 'image/jpeg', 0.92);
    };
    // BUG-SENTRY-005: Add cache-busting param to prevent tainted canvas from cached non-CORS responses.
    // Browsers may serve a cached image without CORS headers if it was first loaded without crossOrigin.
    img.src = `${API_BASE}/api/renders/${render.staged_filename}?_cb=${Date.now()}`;
  };

  // --- DETAIL VIEW ---
  if (detailProperty) {
    // Group renders by room type
    const rendersByRoom: Record<string, Render[]> = {};
    for (const room of ALL_ROOMS) {
      const matching = detailRenders.filter(r => r.room_type === room);
      if (matching.length > 0) rendersByRoom[room] = matching;
    }
    // Collect "other" renders that don't match standard rooms
    const standardRoomSet = new Set<string>(ALL_ROOMS);
    const otherRenders = detailRenders.filter(r => !standardRoomSet.has(r.room_type));

    return (<>
        {deleteDialog}
        <main className="min-h-screen bg-bg">
        <div className="max-w-6xl mx-auto w-full px-6 md:px-10 py-10 md:py-14">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 mb-8 text-sm">
            <button onClick={() => { setDetailProperty(null); load(); }} className="text-ink/55 hover:text-accent transition-colors font-semibold uppercase tracking-[0.11em] text-[11px] flex items-center gap-1.5">
              <ArrowLeft size={13} /> Woningen
            </button>
            <span className="text-ink/25">›</span>
            <span className="font-semibold text-[11px] uppercase tracking-[0.11em] truncate text-ink/70">{detailProperty.address}</span>
            {detailProperty.city && <span className="text-ink/45 text-[11px]">{detailProperty.city}</span>}
          </div>
          <PropertyDetailHeader
            property={detailProperty}
            exportLabel={exportLabel}
            onEditLabel={setExportLabel}
            onSaveProperty={async (addr, cty, pc) => {
              try {
                await api.patch(`/api/properties/${detailProperty.id}`, { address: addr, city: cty, postalCode: pc });
                setDetailProperty({ ...detailProperty, address: addr, city: cty || null, postal_code: pc || null });
              } catch (err: unknown) {
                alert(err instanceof Error ? err.message : 'Opslaan mislukt');
              }
            }}
            onBulk={() => setShowBulk(!showBulk)}
            onStartStaging={() => setShowSmartUpload(!showSmartUpload)}
            onDelete={() => requestDelete(detailProperty)}
            onTogglePublic={() => {
              if (detailProperty.slug) window.open(`/p/${detailProperty.slug}`, '_blank');
              api.patch(`/api/properties/${detailProperty.id}/public`, {}).catch(() => {});
            }}
          />

          {/* Presentatiepagina */}
          {detailProperty.slug && !rendersLoading && detailRenders.length > 0 && (
            <div className="flex items-center gap-3 mb-6 p-4 bg-white border border-black/8 rounded-xl">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-ink/55 uppercase tracking-[0.11em] font-semibold mb-1">Presentatiepagina</p>
                <p className="text-xs text-ink/55 truncate">
                  {window.location.origin}/p/{detailProperty.slug}
                </p>
              </div>
              <PresentationLink property={{ ...detailProperty, render_count: detailRenders.length }} />
            </div>
          )}

          {/* Start staging panel — opent direct de OS file picker; classificatie start auto. */}
          {showSmartUpload && (
            <div className="bg-surface border border-border rounded-lg p-5 shadow-card mb-6">
              <SmartUpload
                autoOpenPicker
                prescopedRoom={smartUploadRoom ?? undefined}
                onStartRestyling={(photos, style) => {
                  setShowSmartUpload(false);
                  setSmartUploadRoom(null);
                  if (photos.length === 0) return;
                  if (photos.length === 1 || !onBatchRestyle) {
                    onSelect(detailProperty, photos[0].roomType, photos[0].file, style);
                  } else {
                    onBatchRestyle(detailProperty, photos, style);
                  }
                }}
              />
            </div>
          )}

          {/* Room scorecard removed - see compact pill row below */}

          {showBulk && !rendersLoading && (
            <BulkGeneratePanel
              propertyId={detailProperty.id}
              mode={AppMode.FULL_RENOVATION}
              uncoveredRooms={new Set(ALL_ROOMS.filter(r => !detailRenders.some(dr => dr.room_type === r)))}
              onComplete={async () => {
                setDetailRenders(await api.get<Render[]>(`/api/properties/${detailProperty.id}/renders`));
                setShowBulk(false);
              }}
              onCreditsUpdate={(credits) => onCreditsUpdate?.(credits)}
            />
          )}

          {rendersLoading ? (
            <div className="flex items-center justify-center py-20 text-ink/50">
              <Loader2 size={20} className="animate-spin mr-2" /> Laden...
            </div>
          ) : detailRenders.length === 0 ? (
            <div className="text-center py-16 md:py-20 bg-white border border-black/8 rounded-2xl">
              <div className="w-14 h-14 mx-auto mb-5 rounded-xl bg-accent/10 text-accent grid place-items-center">
                <ImageIcon size={28} />
              </div>
              <p className="font-sans text-xl font-semibold tracking-[-0.01em] mb-2">Nog geen restylings</p>
              <p className="text-sm text-ink/55 mb-7">Start hieronder met je eerste restyling voor deze woning.</p>
              <button
                onClick={() => setShowSmartUpload(true)}
                className="bg-accent hover:bg-accent-hover text-white px-5 py-3 rounded-lg font-semibold text-sm inline-flex items-center gap-2 shadow-[0_6px_18px_-6px_rgba(241,110,0,0.5)] hover:shadow-[0_10px_26px_-8px_rgba(241,110,0,0.55)] hover:-translate-y-0.5 transition-all"
              >
                <Camera size={15} /> Start staging
              </button>
            </div>
          ) : (
            <div className="space-y-10">
              {/* HERO: 4 grote tiles, één per hoofdruimte (laatste render of empty CTA) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {ALL_ROOMS.map(room => {
                  const roomRenders = rendersByRoom[room];
                  const latest = roomRenders?.[0];
                  if (!latest) {
                    return (
                      <button
                        key={room}
                        onClick={() => startStagingForRoom(detailProperty!, room)}
                        className="group bg-white border border-dashed border-black/15 hover:border-accent/60 hover:bg-accent/[0.03] rounded-2xl aspect-[4/3] flex flex-col items-center justify-center gap-3 transition-colors"
                      >
                        <ImageIcon size={30} className="text-ink/25 group-hover:text-accent transition-colors" />
                        <p className="text-sm font-semibold text-ink/65 group-hover:text-ink">{ROOM_LABELS[room]}</p>
                        <span className="text-[10px] uppercase tracking-[0.1em] font-semibold text-ink/40 group-hover:text-accent inline-flex items-center gap-1.5 transition-colors">
                          <Camera size={11}/> Start
                        </span>
                      </button>
                    );
                  }
                  return (
                    <div
                      key={room}
                      className="group bg-white border border-black/8 hover:border-accent/40 rounded-2xl overflow-hidden cursor-pointer transition-all hover:shadow-[0_12px_30px_-12px_rgba(241,110,0,0.3)]"
                      onClick={() => setLightboxRender(latest)}
                    >
                      <div className="relative aspect-[4/3] bg-stone-100 overflow-hidden">
                        {latest.original_filename ? (
                          <ComparisonSlider
                            beforeImage={`${API_BASE}/api/renders/${latest.original_filename}`}
                            afterImage={`${API_BASE}/api/renders/${latest.staged_filename}`}
                            labelBefore="ORIGINEEL"
                            labelAfter={exportLabel}
                          />
                        ) : (
                          <img
                            src={`${API_BASE}/api/renders/${latest.staged_filename}`}
                            alt={`${ROOM_LABELS[room]} laatste restyling`}
                            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                          />
                        )}
                        <div className="absolute top-3 left-3 inline-flex items-center gap-2 z-10 pointer-events-none">
                          <span className="bg-white/95 backdrop-blur text-ink text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">{ROOM_LABELS[room]}</span>
                          {roomRenders.length > 1 && (
                            <span className="bg-ink/80 text-white text-[10px] font-semibold px-2 py-1 rounded-full">+{roomRenders.length - 1}</span>
                          )}
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); onSelect(detailProperty, latest.room_type); }}
                          className="absolute bottom-3 right-3 z-10 bg-ink/80 hover:bg-accent text-white text-[10px] font-bold uppercase tracking-[0.08em] px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-all inline-flex items-center gap-1.5"
                        >
                          <Camera size={10} /> + Nieuwe restyling
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* CORRECTIES & GESCHIEDENIS: alle niet-hero renders (oude versies + other rooms) */}
              {(() => {
                const historyRenders: Render[] = [];
                for (const room of ALL_ROOMS) {
                  const list = rendersByRoom[room];
                  if (list && list.length > 1) historyRenders.push(...list.slice(1));
                }
                historyRenders.push(...otherRenders);
                if (historyRenders.length === 0) return null;
                return (
                  <div>
                    <div className="flex items-end justify-between gap-4 mb-5">
                      <div>
                        <p className="text-xs font-semibold text-accent uppercase tracking-[0.1em] mb-1.5">
                          Archief
                        </p>
                        <h2 className="font-sans text-xl md:text-2xl font-bold text-ink tracking-[-0.02em]">Correcties &amp; geschiedenis</h2>
                        <p className="text-sm text-ink/55 mt-1">{historyRenders.length} render{historyRenders.length !== 1 ? 's' : ''} · klik op een thumbnail om te bekijken of opnieuw te genereren.</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                      {historyRenders.map(r => {
                        const typeInfo = TYPE_LABELS[r.generation_type] || { label: r.generation_type, color: '#eee' };
                        return (
                          <div
                            key={r.id}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                                e.preventDefault();
                                setLightboxRender(r);
                              }
                            }}
                            onClick={() => setLightboxRender(r)}
                            className="group bg-white border border-black/8 rounded-2xl overflow-hidden text-left hover:border-accent/40 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.06)] hover:shadow-[0_12px_32px_-12px_rgba(0,0,0,0.18)] transition-all"
                          >
                            <div className="relative aspect-[4/3] bg-stone-100 overflow-hidden">
                              <img src={`${API_BASE}/api/renders/${r.staged_filename}`} alt={ROOM_LABELS[r.room_type] || r.room_type} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                              <span className="absolute top-2.5 left-2.5 text-[10px] font-bold uppercase tracking-[0.06em] px-2 py-1 rounded-full shadow-[0_2px_6px_rgba(0,0,0,0.1)]" style={{ background: typeInfo.color, color: '#1a1a1a' }}>{typeInfo.label}</span>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onSelect(detailProperty, r.room_type); }}
                                className="absolute bottom-3 right-3 z-10 bg-ink/80 hover:bg-accent text-white text-[10px] font-bold uppercase tracking-[0.08em] px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 focus:opacity-100 transition-all"
                              >
                                + Nieuwe restyling
                              </button>
                            </div>
                            <div className="px-3 py-2.5 border-t border-black/6">
                              <p className="text-sm font-bold text-ink truncate tracking-[-0.01em]">{ROOM_LABELS[r.room_type] || r.room_type}</p>
                              <p className="text-xs text-ink/50 mt-0.5">{fmt(r.created_at)}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

            </div>
          )}
        </div>
        </main>

        {/* Lightbox */}
        {lightboxRender && (
          <div
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-6 overflow-y-auto"
            onClick={() => { setLightboxRender(null); setRegenFeedback(''); setRegenError(null); }}
          >
            <div
              className="relative w-full max-w-3xl shadow-2xl bg-white rounded-xl overflow-hidden flex flex-col"
              onClick={e => e.stopPropagation()}
            >
              <div style={{ aspectRatio: '4/3' }} className="bg-stone-100">
                {lightboxRender.original_filename ? (
                  <ComparisonSlider
                    beforeImage={`${API_BASE}/api/renders/${lightboxRender.original_filename}`}
                    afterImage={`${API_BASE}/api/renders/${lightboxRender.staged_filename}`}
                    labelBefore="ORIGINEEL"
                    labelAfter={exportLabel}
                  />
                ) : (
                  <img src={`${API_BASE}/api/renders/${lightboxRender.staged_filename}`} alt="render" className="w-full h-full object-cover" />
                )}
              </div>

              {/* Feedback + actions */}
              <div className="p-5 border-t border-border space-y-3">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-ink/60 flex items-center gap-2 mb-1.5">
                    Render opnieuw met feedback
                    <span className="text-ink/40 normal-case tracking-normal font-normal">·  1 credit per nieuwe render</span>
                  </label>
                  <textarea
                    value={regenFeedback}
                    onChange={e => setRegenFeedback(e.target.value)}
                    placeholder="bv. 'verwijder de muur-radiator', 'maak de bank donkergroen', 'minder kussens'"
                    rows={2}
                    disabled={regenLoading || !lightboxRender.original_filename}
                    className="w-full bg-stone-50 border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-ink disabled:opacity-50 resize-none"
                  />
                  {!lightboxRender.original_filename && (
                    <p className="text-[10px] text-ink/40 mt-1">Voor deze render is geen origineel beschikbaar - opnieuw renderen niet mogelijk.</p>
                  )}
                  {regenError && (
                    <p className="text-[11px] text-red-600 mt-1">{regenError}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    disabled={regenLoading || !regenFeedback.trim() || !lightboxRender.original_filename}
                    onClick={async () => {
                      setRegenLoading(true);
                      setRegenError(null);
                      try {
                        const body = await api.post<{ credits?: number }>('/api/ai/regenerate-render', { renderId: lightboxRender.id, feedback: regenFeedback.trim() });
                        // refresh renders + close lightbox
                        if (detailProperty) {
                          setDetailRenders(await api.get<Render[]>(`/api/properties/${detailProperty.id}/renders`));
                        }
                        if (typeof body.credits === 'number') onCreditsUpdate?.(body.credits);
                        setRegenFeedback('');
                        setLightboxRender(null);
                      } catch (e: any) {
                        setRegenError(e.message);
                      } finally {
                        setRegenLoading(false);
                      }
                    }}
                    className="flex-1 bg-accent hover:bg-accent-hover text-white font-semibold tracking-[-0.005em] text-sm py-3 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed shadow-[0_6px_18px_-6px_rgba(241,110,0,0.5)] hover:shadow-[0_10px_26px_-8px_rgba(241,110,0,0.55)] disabled:shadow-none"
                  >
                    {regenLoading ? <><Loader2 size={14} className="animate-spin"/> Renderen...</> : <>Render opnieuw met feedback</>}
                  </button>
                  <button
                    onClick={() => handleGalleryDownload(lightboxRender)}
                    disabled={regenLoading}
                    className="bg-ink text-white font-bold uppercase tracking-wide text-xs px-5 py-3 rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-40"
                  >
                    <Download size={14} /> Download
                  </button>
                </div>
                <button
                  onClick={() => { setLightboxRender(null); setRegenFeedback(''); onSelect(detailProperty!, lightboxRender.room_type); }}
                  className="w-full text-xs text-ink/45 hover:text-accent transition-colors py-1.5 flex items-center justify-center gap-1.5"
                >
                  <Camera size={11} /> Nieuwe restyling starten
                </button>
              </div>
            </div>
            <button
              className="absolute top-4 right-4 text-white/70 hover:text-white text-2xl font-bold leading-none"
              onClick={() => { setLightboxRender(null); setRegenFeedback(''); setRegenError(null); }}
            >&#x2715;</button>
          </div>
        )}
    </>);
  }

  // --- LIST VIEW ---
  return (
      <main className="min-h-screen bg-bg">
        {deleteDialog}
        <div className="max-w-6xl mx-auto w-full px-6 md:px-10 py-10 md:py-14">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink/45 mb-2">Werk</p>
            <h1 className="font-sans text-3xl md:text-4xl font-bold tracking-[-0.02em] text-ink">Woningen</h1>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center bg-ink/5 rounded-full p-1">
              <button onClick={() => { setListMode('grid'); localStorage.setItem('er_list_mode_v2', 'grid'); }}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${listMode === 'grid' ? 'bg-white text-ink shadow-sm' : 'text-ink/50 hover:text-ink'}`}>
                Grid
              </button>
              <button onClick={() => { setListMode('table'); localStorage.setItem('er_list_mode_v2', 'table'); }}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${listMode === 'table' ? 'bg-white text-ink shadow-sm' : 'text-ink/50 hover:text-ink'}`}>
                Tabel
              </button>
            </div>
            <button
              onClick={() => setShowAdd(!showAdd)}
              className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition-all shadow-[0_6px_18px_-6px_rgba(241,110,0,0.5)] hover:shadow-[0_10px_26px_-8px_rgba(241,110,0,0.55)] hover:-translate-y-0.5"
            >
              <Plus size={14} /> Woning toevoegen
            </button>
          </div>
        </div>

        {showAdd && (
          <form onSubmit={handleAdd} className="bg-white border border-black/8 rounded-2xl p-5 md:p-6 mb-6 animate-in slide-in-from-top-2 duration-200">
            <p className="text-[11px] font-semibold uppercase tracking-[0.11em] text-ink/45 mb-4">Nieuwe woning</p>
            <div className="grid grid-cols-12 gap-3 mb-3">
              <div className="col-span-12 md:col-span-6">
                <label className="block text-[11px] font-semibold text-ink/55 uppercase tracking-[0.08em] mb-1.5">Adres *</label>
                <input
                  type="text" value={address} onChange={e => setAddress(e.target.value)}
                  placeholder="Hoofdstraat 1" required autoFocus
                  autoComplete="street-address"
                  className="w-full bg-white border border-black/10 rounded-lg p-2.5 text-sm focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all"
                />
              </div>
              <div className="col-span-5 md:col-span-3">
                <label className="block text-[11px] font-semibold text-ink/55 uppercase tracking-[0.08em] mb-1.5">Postcode</label>
                <input
                  type="text" value={postalCode} onChange={e => setPostalCode(e.target.value)}
                  placeholder="1012 AB"
                  autoComplete="postal-code"
                  className="w-full bg-white border border-black/10 rounded-lg p-2.5 text-sm focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all"
                />
              </div>
              <div className="col-span-7 md:col-span-3">
                <label className="block text-[11px] font-semibold text-ink/55 uppercase tracking-[0.08em] mb-1.5">Stad</label>
                <input
                  type="text" value={city} onChange={e => setCity(e.target.value)}
                  placeholder="Amsterdam"
                  autoComplete="address-level2"
                  className="w-full bg-white border border-black/10 rounded-lg p-2.5 text-sm focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all"
                />
              </div>
            </div>
            {error && <p className="text-red-600 text-xs mb-3">{error}</p>}
            <div className="flex gap-2">
              <button type="submit" disabled={saving}
                className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-60"
              >
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                Opslaan
              </button>
              <button type="button" onClick={() => setShowAdd(false)}
                className="px-4 py-2.5 rounded-lg text-sm font-semibold text-ink/55 hover:text-ink hover:bg-ink/5 transition-colors"
              >
                Annuleren
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16 text-ink/50">
            <Loader2 size={20} className="animate-spin mr-2" /> Laden...
          </div>
        ) : properties.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 rounded-2xl bg-white border border-black/8">
            <div className="w-16 h-16 bg-accent text-white flex items-center justify-center rounded-2xl mb-6 shadow-[0_8px_24px_-8px_rgba(241,110,0,0.5)]">
              <Plus size={28} />
            </div>
            <h2 className="font-sans text-2xl font-bold tracking-[-0.015em] mb-2">Voeg je eerste woning toe</h2>
            <p className="text-sm text-ink/55 mb-8 text-center max-w-sm leading-relaxed">
              Start met renderen. Upload een foto, AI doet de rest.
            </p>
            <button onClick={() => setShowAdd(true)}
              className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white px-6 py-3 rounded-lg text-sm font-semibold transition-all shadow-[0_6px_18px_-6px_rgba(241,110,0,0.5)] hover:shadow-[0_10px_26px_-8px_rgba(241,110,0,0.55)] hover:-translate-y-0.5"
            >
              <Plus size={16} /> Woning toevoegen
            </button>
          </div>
        ) : (<>
          {/* Woningen */}
          {listMode === 'table' ? (
          <PropertyTable
            properties={properties}
            onOpen={openDetail}
            onDelete={handleDelete}
            onSelectRoom={(p, room) => startStagingForRoom(p, room)}
            fmt={fmt}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {properties.map(p => {
              const roomThumbs: { room: string; thumb: string | null }[] = [
                { room: 'Living Room', thumb: p.thumb_living_room },
                { room: 'Bedroom',     thumb: p.thumb_bedroom },
                { room: 'Kitchen',     thumb: p.thumb_kitchen },
                { room: 'Bathroom',    thumb: p.thumb_bathroom },
              ];
              const covered = parseCoveredRooms(p.covered_rooms);
              const pct = roomCoveragePct(covered);
              const subtitleParts = [p.postal_code, p.city].filter(Boolean);
              return (
                <div
                  key={p.id}
                  className="bg-white border border-black/8 rounded-2xl overflow-hidden hover:border-black/15 hover:shadow-[0_12px_32px_-16px_rgba(0,0,0,0.2)] transition-all group cursor-pointer flex flex-col"
                  onClick={() => openDetail(p)}
                >
                  {/* Header */}
                  <div className="px-5 pt-5 pb-4 flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-base leading-tight truncate tracking-[-0.01em]">{p.address}</p>
                      {subtitleParts.length > 0 && (
                        <p className="text-xs text-ink/55 mt-0.5 truncate">{subtitleParts.join(' · ')}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <PresentationLink property={p} />
                      <button onClick={(e) => handleDelete(p.id, e)} className="p-1.5 text-ink/35 hover:text-red-600 transition-colors rounded-md hover:bg-red-50" title="Verwijderen">
                        <Trash2 size={13} />
                      </button>
                      <ChevronRight size={16} className="text-ink/35 group-hover:text-accent transition-colors ml-1" />
                    </div>
                  </div>

                  {/* Dekking - prominente progress + chips */}
                  <div className="px-5 pb-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-[10.5px] font-semibold uppercase tracking-[0.11em] text-ink/55">Dekking</span>
                        <span className={`text-[11px] font-bold ${pct === 100 ? 'text-accent' : 'text-ink'}`}>
                          {coveredRoomCount(covered)}/{coverageDenominator(covered)}
                        </span>
                      </div>
                      <span className={`text-[10px] font-bold ${pct === 100 ? 'text-accent' : 'text-ink/60'}`}>{pct}%</span>
                    </div>
                    <div className="w-full bg-black/5 h-1.5 rounded-full overflow-hidden mb-3">
                      <div className="h-full bg-accent transition-all duration-500" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {ALL_ROOMS.map(room => {
                        const done = covered.has(room);
                        return (
                          <span
                            key={room}
                            onClick={(e) => { e.stopPropagation(); if (!done) startStagingForRoom(p, room); }}
                            className={`inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-0.5 rounded-md transition-colors ${
                              done
                                ? 'bg-accent/12 text-accent border border-accent/25'
                                : 'bg-black/5 text-ink/55 border border-black/8 hover:bg-black/10 cursor-pointer'
                            }`}
                            title={done ? `${ROOM_LABELS[room]} klaar` : `Start ${ROOM_LABELS[room]}`}
                          >
                            {done ? <Check size={10} strokeWidth={3} /> : <Plus size={10} strokeWidth={2.5} />}
                            {ROOM_LABELS[room]}
                          </span>
                        );
                      })}
                    </div>
                  </div>

                  {/* Room thumbnail row - 4 hoofdruimtes */}
                  <div className="px-5 pb-5">
                    <div className="grid grid-cols-4 gap-2">
                      {roomThumbs.map(({ room, thumb }) => (
                        <button
                          key={room}
                          onClick={(e) => { e.stopPropagation(); if (!thumb) startStagingForRoom(p, room); else openDetail(p); }}
                          className={`group/tile relative aspect-[4/3] rounded-lg overflow-hidden border transition-all ${
                            thumb
                              ? 'border-black/8 hover:border-accent/40'
                              : 'border-dashed border-black/15 hover:border-accent/40 bg-black/[0.02]'
                          }`}
                          title={thumb ? ROOM_LABELS[room] : `${ROOM_LABELS[room]}: start`}
                        >
                          {thumb ? (
                            <>
                              <img src={`${API_BASE}/api/renders/${thumb}`} alt={ROOM_LABELS[room]} className="w-full h-full object-cover group-hover/tile:scale-[1.04] transition-transform duration-300" />
                              <span className="absolute left-1 bottom-1 text-[9px] font-semibold tracking-tight bg-white/92 text-ink px-1.5 py-0.5 rounded-sm backdrop-blur-sm">{ROOM_LABELS[room]}</span>
                            </>
                          ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-ink/35 group-hover/tile:text-accent transition-colors">
                              <Camera size={14} />
                              <span className="text-[9px] font-semibold uppercase tracking-wider">{ROOM_LABELS[room]}</span>
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                    <p className="text-[10.5px] text-ink/50 mt-3 font-medium">
                      {p.render_count === 0 ? 'Nog geen renders' : `${p.render_count} render${p.render_count !== 1 ? 's' : ''}`}
                      {p.last_render_at ? ` · bewerkt ${timeAgo(p.last_render_at)}` : ''}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

          {/* Stats - secundair, onderaan */}
          {properties.length > 0 && (
            <div className="mt-8 pt-6 border-t border-border/10 flex items-center gap-6 text-center">
              <div>
                <p className="text-lg font-bold">{properties.length}</p>
                <p className="text-[9px] text-ink/50 uppercase tracking-wide">Woningen</p>
              </div>
              <div>
                <p className="text-lg font-bold">{properties.reduce((s, p) => s + p.render_count, 0)}</p>
                <p className="text-[9px] text-ink/50 uppercase tracking-wide">Restylings</p>
              </div>
              <div>
                <p className="text-lg font-bold">
                  {properties.reduce((s, p) => s + coveredRoomCount(parseCoveredRooms(p.covered_rooms)), 0)}/{properties.reduce((s, p) => s + coverageDenominator(parseCoveredRooms(p.covered_rooms)), 0)}
                </p>
                <p className="text-[9px] text-ink/50 uppercase tracking-wide">Ruimtes klaar</p>
              </div>
            </div>
          )}
        </>)}
        </div>
      </main>
  );
};
